import type { ChatbotVendorAdapter } from '../types';
import { GIGA_BRANDING } from './config';
import { injectGigaWidget, isGigaMounted, removeGigaWidget } from './dom';
import { mapContextToGiga } from './mapContextToGiga';
import type { GigaWidgetConfig } from './types';

const noop = (): void => {};

/**
 * Giga adapter. Giga ships a self-injecting loader driven by `window.CHAT_WIDGET_CONFIG` and exposes
 * no runtime JS API: visibility and metadata are fixed at load, and a change requires remounting
 * (which the provider does by rebuilding the instance on config change). Runtime controls and event
 * subscriptions are therefore unsupported no-ops.
 *
 * TODO(WEB-6376): branding (company_name, colors, tagline, icon_url), agent_template_id and org_id
 * still need real values wired from app config.
 */
export const gigaAdapter: ChatbotVendorAdapter = {
  // Config must precede the loader, so the script is injected together with it in injectElement.
  injectScript: noop,
  injectElement: (config, logger) => {
    if (!config.agentId) {
      logger.error({
        type: 'client',
        context: 'Chatbot - giga.injectElement',
        message: 'No agent template id provided',
        sessionId: undefined,
      });
      return;
    }

    const widgetConfig: GigaWidgetConfig = {
      ...GIGA_BRANDING,
      agent_template_id: config.agentId,
      initialization_values: mapContextToGiga(config.metadata?.context ?? {}),
      on_error: ({ message, error, source, timestamp }) => {
        const detail = error instanceof Error ? `: ${error.message}` : '';
        logger.error(
          {
            type: 'client',
            context: 'Chatbot - giga.onError',
            message: `Giga widget error (${source}): ${message}${detail}`,
            sessionId: undefined,
          },
          { error, timestamp },
        );
      },
    };
    injectGigaWidget(widgetConfig, logger);
  },
  removeElement: (logger) => removeGigaWidget(logger),
  removeScript: noop,
  isMounted: () => isGigaMounted(),
  // Giga seeds metadata at load; the provider remounts on config change, so live updates are no-ops.
  setMetadata: noop,
  show: noop,
  hide: noop,
  setOpen: noop,
  setOnMessage: noop,
  subscribeToEvent: () => -1,
  unsubscribeFromEvent: noop,
  fireEvent: noop,
  trackEvent: noop,
};
